import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc
} from "firebase/firestore";
import { db } from "@/firebase";
import {
  DEFAULT_FEEDBACK_LIMIT_PERIOD,
  DEFAULT_FREE_FEEDBACK_LIMIT,
  DEFAULT_PRO_FEEDBACK_LIMIT,
  FREE_PLAN
} from "@/services/subscriptionAccess";

function normalizeDisplayName(authUser, fallback = "") {
  return (
    (typeof authUser?.displayName === "string" && authUser.displayName.trim()) ||
    (typeof fallback === "string" && fallback.trim()) ||
    ""
  );
}

function buildDefaultProfileData(authUser, profileOverrides = {}) {
  return {
    uid: authUser.uid,
    email: authUser.email || "",
    displayName: normalizeDisplayName(authUser, profileOverrides.displayName),
    plan: FREE_PLAN,
    role: "student",
    feedbackCount: 0,
    freeFeedbackLimit: DEFAULT_FREE_FEEDBACK_LIMIT,
    proFeedbackLimit: DEFAULT_PRO_FEEDBACK_LIMIT,
    feedbackLimitPeriod: DEFAULT_FEEDBACK_LIMIT_PERIOD,
    billingProvider: null,
    subscriptionStatus: null,
    subscriptionId: null,
    stripeCustomerId: null,
    stripePriceId: null,
    currentPeriodEnd: null,
    lastFeedbackAt: null,
    ...profileOverrides
  };
}

export async function ensureUserProfileDocument(authUser, profileOverrides = {}) {
  if (!authUser || !authUser.uid) {
    throw new Error("Cannot create profile without an authenticated user.");
  }

  const userRef = doc(db, "users", authUser.uid);
  const userSnap = await getDoc(userRef);

  const defaultProfileData = buildDefaultProfileData(authUser, profileOverrides);
  const existingData = userSnap.exists() ? userSnap.data() : {};
  const resolvedPlan = existingData.plan || defaultProfileData.plan;

  await setDoc(
    userRef,
    {
      ...defaultProfileData,
      ...existingData,
      email: authUser.email || existingData.email || defaultProfileData.email,
      displayName: normalizeDisplayName(
        authUser,
        existingData.displayName || defaultProfileData.displayName
      ),
      plan: resolvedPlan,
      role: existingData.role || defaultProfileData.role,
      feedbackCount:
        typeof existingData.feedbackCount === "number"
          ? existingData.feedbackCount
          : defaultProfileData.feedbackCount,
      freeFeedbackLimit:
        typeof existingData.freeFeedbackLimit === "number"
          ? existingData.freeFeedbackLimit
          : defaultProfileData.freeFeedbackLimit,
      proFeedbackLimit:
        existingData.proFeedbackLimit === null ||
        typeof existingData.proFeedbackLimit === "number"
          ? existingData.proFeedbackLimit
          : defaultProfileData.proFeedbackLimit,
      feedbackLimitPeriod:
        existingData.feedbackLimitPeriod ||
        defaultProfileData.feedbackLimitPeriod,
      billingProvider:
        existingData.billingProvider ?? defaultProfileData.billingProvider,
      subscriptionStatus:
        existingData.subscriptionStatus ?? defaultProfileData.subscriptionStatus,
      subscriptionId:
        existingData.subscriptionId ?? defaultProfileData.subscriptionId,
      stripeCustomerId:
        existingData.stripeCustomerId ?? defaultProfileData.stripeCustomerId,
      stripePriceId:
        existingData.stripePriceId ?? defaultProfileData.stripePriceId,
      currentPeriodEnd:
        existingData.currentPeriodEnd ?? defaultProfileData.currentPeriodEnd,
      lastFeedbackAt:
        existingData.lastFeedbackAt ?? defaultProfileData.lastFeedbackAt,
      createdAt: existingData.createdAt || serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // TODO: Map these billing fields from Stripe customer/subscription data
      // once checkout is added.
    },
    { merge: true }
  );

  const refreshed = await getDoc(userRef);
  return refreshed.exists() ? refreshed.data() : defaultProfileData;
}

export async function saveEssaySubmission({ uid, prompt, writing, feedback }) {
  if (!uid) {
    throw new Error("Missing uid for essay submission.");
  }

  const submissionsRef = collection(db, "users", uid, "essaySubmissions");
  const payload = {
    promptId: prompt?.id || null,
    promptText: prompt?.text || "",
    moduleVersion: prompt?.moduleVersion || null,
    writing: writing || {},
    feedback: feedback || {},
    overallScore:
      typeof feedback?.overallScore === "number" ? feedback.overallScore : null,
    createdAt: serverTimestamp(),
    submittedAt: serverTimestamp()
  };

  return addDoc(submissionsRef, payload);
}
